import { GatsbyImageProps, IGatsbyImageData } from 'gatsby-plugin-image';
import {
  ChildImageSharpProp,
  GatsbyImageDataProp,
} from '../components/common/horizontal-card/types';
import { ContentfulGatsbyImage } from '../types/images';

/** Check that the image data actually contains a renderable src */
const hasValidSrc = (image?: IGatsbyImageData): image is IGatsbyImageData =>
  !!image?.images?.fallback?.src ||
  !!image?.images?.sources?.some(s => s.srcSet);

export const isIGatsbyImageData = (
  source: ContentfulGatsbyImage | GatsbyImageDataProp | ChildImageSharpProp,
): source is GatsbyImageDataProp =>
  !!(source as GatsbyImageDataProp).gatsbyImageData;

export const isChildImageSharp = (
  source: ContentfulGatsbyImage | GatsbyImageDataProp | ChildImageSharpProp,
): source is ChildImageSharpProp =>
  !!(source as ChildImageSharpProp).childImageSharp;

const useContentfulGatsbyImage = (
  source?: ContentfulGatsbyImage | GatsbyImageDataProp | ChildImageSharpProp,
): GatsbyImageProps | undefined => {
  if (!source) return undefined;
  if (isIGatsbyImageData(source)) {
    if (!hasValidSrc(source.gatsbyImageData)) return undefined;
    return {
      alt: '',
      image: source.gatsbyImageData,
    };
  }
  if (isChildImageSharp(source)) {
    if (!hasValidSrc(source.childImageSharp.image)) return undefined;
    return {
      alt: '',
      image: source.childImageSharp.image,
    };
  }

  const { image, description, title } = source;

  if (!hasValidSrc(image)) {
    return undefined;
  }

  return {
    alt: description || title || '',
    image,
  };
};

export default useContentfulGatsbyImage;
